import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { PageHeader } from "@/components/shared";
import { useFilter } from "@/context/FilterContext";
import { useSettings } from "@/hooks/useSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Target, Plus } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = { Active: "rag-amber", Achieved: "rag-green", Missed: "rag-red", Revised: "rag-amber" };
const curMonth = () => new Date().toISOString().slice(0, 7);
const emptyForm = () => ({ role: "", metric_key: "", metric_label: "", target_value: "", unit: "", weightage: "", period: "Monthly", month: curMonth(), owner: "", start_date: "", end_date: "", weekly_targets: ["", "", "", ""] });

function achievementPct(actual, target, direction) {
  const a = Number(actual) || 0, t = Number(target) || 0;
  if (!t) return 0;
  const pct = direction === "lower" ? (a === 0 ? 100 : (t / a) * 100) : (a / t) * 100;
  return Math.round(pct * 10) / 10;
}
function status(pct) { return pct >= 100 ? "green" : pct >= 80 ? "amber" : "red"; }

export default function GoalSetting() {
  const qc = useQueryClient();
  const { role: fRole, employee: fEmp } = useFilter();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const { data: goals = [] } = useQuery({ queryKey: ["goals"], queryFn: async () => (await api.get("/goals")).data });
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });
  const { data: kpis = [] } = useQuery({ queryKey: ["kpi-configs"], queryFn: async () => (await api.get("/kpi-configs")).data });
  const { data: dash } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  const actualMap = {};
  (dash?.blocks || []).forEach((b) => b.metrics.forEach((m) => { actualMap[m.key] = m.actual; }));

  const saveMut = useMutation({
    mutationFn: async (payload) => api.post("/goals", payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["goals"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); setOpen(false); toast.success("Goal created — reflected on the Dashboard scorecard"); },
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, body }) => api.put(`/goals/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["goals"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
  });

  const roleKpis = kpis.filter((k) => k.role === form.role && k.active !== "N");
  const roleOwners = team.filter((t) => t.role === form.role);

  const onPickKpi = (metric_key) => {
    const k = roleKpis.find((x) => x.metric_key === metric_key);
    if (!k) { setForm({ ...form, metric_key, metric_label: "" }); return; }
    const t = Number(k.default_target) || 0;
    const wk = Math.round((t / 4) * 10) / 10;
    setForm({ ...form, metric_key, metric_label: k.name, unit: k.unit || "", weightage: k.weightage ?? "", target_value: t, weekly_targets: [wk, wk, wk, t - wk * 3] });
  };

  const onTarget = (v) => {
    const t = Number(v) || 0; const wk = Math.round((t / 4) * 10) / 10;
    setForm({ ...form, target_value: v, weekly_targets: [wk, wk, wk, Math.round((t - wk * 3) * 10) / 10] });
  };

  const submit = () => {
    if (!form.role || !form.metric_key || form.target_value === "") { toast.error("Fill role, KPI and target"); return; }
    const dir = roleKpis.find((k) => k.metric_key === form.metric_key)?.direction || "higher";
    saveMut.mutate({ ...form, target_value: Number(form.target_value), weightage: Number(form.weightage) || 0, weekly_targets: form.weekly_targets.map((w) => Number(w) || 0), direction: dir, status: "Active" });
  };

  const matchFilter = (g) => (!fRole || g.role === fRole) && (!fEmp || g.owner === fEmp);
  const active = goals.filter((g) => g.status === "Active" && matchFilter(g));
  const history = goals.filter((g) => g.status !== "Active" && matchFilter(g));

  return (
    <div>
      <PageHeader title="Goal Setting" subtitle="Set a monthly target per KPI — auto-split into weekly targets; actuals come from activity records automatically">
        <Button data-testid="add-goal-btn" onClick={() => { setForm(emptyForm()); setOpen(true); }} className="bg-gold text-navy hover:bg-gold-hover gap-2">
          <Plus className="w-4 h-4" /> Add New Goal
        </Button>
      </PageHeader>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {active.map((g) => {
          const actual = actualMap[g.metric_key] ?? 0;
          const pct = achievementPct(actual, g.target_value, g.direction);
          const gap = Math.max(0, Number(g.target_value) - Number(actual));
          const rag = status(pct);
          return (
            <div key={g.id} className="rounded-lg border border-surface-hover bg-surface p-5" data-testid={`goal-${g.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs px-2 py-0.5 rounded bg-gold/15 text-gold">{g.role}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[g.status]}`}>{g.status}</span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-gold" />
                <span className="font-heading font-bold">{g.metric_label}</span>
              </div>
              <div className="flex items-end gap-2 my-2">
                <div className="text-3xl font-heading font-extrabold">{Number(actual).toLocaleString("en-IN")}</div>
                <div className="text-sm text-slatetext mb-1">/ {Number(g.target_value).toLocaleString("en-IN")} {g.unit}</div>
                <span className={`ml-auto mb-1 w-2.5 h-2.5 rounded-full ${rag === "green" ? "bg-[#059669]" : rag === "amber" ? "bg-[#F59E0B]" : "bg-[#DC2626]"}`} />
              </div>
              <div className="h-1.5 w-full rounded-full bg-navy overflow-hidden mb-2">
                <div className={`h-full ${rag === "green" ? "bg-[#059669]" : rag === "amber" ? "bg-[#F59E0B]" : "bg-[#DC2626]"}`} style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              <div className="flex items-center justify-between text-xs text-slatetext mb-2">
                <span>Achievement {pct}%</span><span>Gap {Number(gap).toLocaleString("en-IN")}</span>
              </div>
              {Array.isArray(g.weekly_targets) && g.weekly_targets.length > 0 && (
                <div className="grid grid-cols-4 gap-1 mb-2" data-testid={`goal-weekly-${g.id}`}>
                  {g.weekly_targets.map((w, i) => (
                    <div key={i} className="text-center bg-navy rounded py-1">
                      <div className="text-[10px] text-slatetext">W{i + 1}</div>
                      <div className="text-xs font-medium">{Number(w).toLocaleString("en-IN")}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-xs text-slatetext mb-2">{g.month || g.period} · Owner: {g.owner || "—"} · Weight {g.weightage || 0}</div>
              <div className="flex gap-2">
                <button data-testid={`goal-achieved-${g.id}`} onClick={() => updateMut.mutate({ id: g.id, body: { status: "Achieved" } })} className="text-xs px-2 py-1 rounded bg-navy border border-surface-hover hover:border-[#059669] transition-colors duration-200">Mark Achieved</button>
                <button data-testid={`goal-missed-${g.id}`} onClick={() => updateMut.mutate({ id: g.id, body: { status: "Missed" } })} className="text-xs px-2 py-1 rounded bg-navy border border-surface-hover hover:border-[#DC2626] transition-colors duration-200">Mark Missed</button>
              </div>
            </div>
          );
        })}
        {active.length === 0 && <p className="text-slatetext">No active goals for this filter. Add one to populate the dashboard.</p>}
      </div>

      <h2 className="font-heading text-lg font-bold mb-3">Goal History</h2>
      <div className="rounded-lg border border-surface-hover bg-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slatetext text-xs uppercase border-b border-surface-hover">
              <th className="px-4 py-3 text-left">Month</th>
              <th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-left">KPI</th>
              <th className="px-4 py-3 text-right">Target</th>
              <th className="px-4 py-3 text-left">Owner</th>
              <th className="px-4 py-3 text-left">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {history.map((g) => (
              <tr key={g.id} className="border-b border-surface-hover/60 hover:bg-surface-hover/40 transition-colors duration-200">
                <td className="px-4 py-3">{g.month || g.period}</td>
                <td className="px-4 py-3">{g.role}</td>
                <td className="px-4 py-3">{g.metric_label}</td>
                <td className="px-4 py-3 text-right tabular-nums">{Number(g.target_value).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">{g.owner || "—"}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[g.status]}`}>{g.status}</span></td>
              </tr>
            ))}
            {history.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slatetext">No past goals yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-surface-hover text-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid="goal-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Goal</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slatetext text-xs mb-1.5 block">Role</Label>
                <select data-testid="goal-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, metric_key: "", owner: "" })} className="w-full bg-navy border border-surface-hover rounded-md px-3 py-2 text-sm focus:border-gold outline-none">
                  <option value="">Select role…</option>
                  {[...new Set(kpis.map((k) => k.role))].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-slatetext text-xs mb-1.5 block">Month</Label>
                <Input data-testid="goal-month" type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="bg-navy border-surface-hover text-white focus:border-gold" />
              </div>
            </div>
            <div>
              <Label className="text-slatetext text-xs mb-1.5 block">KPI</Label>
              <select data-testid="goal-metric" value={form.metric_key} onChange={(e) => onPickKpi(e.target.value)} disabled={!form.role} className="w-full bg-navy border border-surface-hover rounded-md px-3 py-2 text-sm focus:border-gold outline-none disabled:opacity-50">
                <option value="">Select KPI…</option>
                {roleKpis.map((k) => <option key={k.metric_key} value={k.metric_key}>{k.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-slatetext text-xs mb-1.5 block">Target</Label>
                <Input data-testid="goal-target" type="number" value={form.target_value} onChange={(e) => onTarget(e.target.value)} className="bg-navy border-surface-hover text-white focus:border-gold" />
              </div>
              <div>
                <Label className="text-slatetext text-xs mb-1.5 block">Unit</Label>
                <Input data-testid="goal-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="bg-navy border-surface-hover text-white focus:border-gold" />
              </div>
              <div>
                <Label className="text-slatetext text-xs mb-1.5 block">Weightage</Label>
                <Input data-testid="goal-weightage" type="number" value={form.weightage} onChange={(e) => setForm({ ...form, weightage: e.target.value })} className="bg-navy border-surface-hover text-white focus:border-gold" />
              </div>
            </div>
            <div>
              <Label className="text-slatetext text-xs mb-1.5 block">Weekly Targets (auto-split, editable)</Label>
              <div className="grid grid-cols-4 gap-2" data-testid="goal-weekly-inputs">
                {form.weekly_targets.map((w, i) => (
                  <Input key={i} type="number" value={w} onChange={(e) => { const arr = [...form.weekly_targets]; arr[i] = e.target.value; setForm({ ...form, weekly_targets: arr }); }} className="bg-navy border-surface-hover text-white focus:border-gold text-center" />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slatetext text-xs mb-1.5 block">Start Date</Label>
                <Input data-testid="goal-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-navy border-surface-hover text-white focus:border-gold" />
              </div>
              <div>
                <Label className="text-slatetext text-xs mb-1.5 block">End Date</Label>
                <Input data-testid="goal-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-navy border-surface-hover text-white focus:border-gold" />
              </div>
            </div>
            <div>
              <Label className="text-slatetext text-xs mb-1.5 block">Owner / Employee</Label>
              <select data-testid="goal-owner" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="w-full bg-navy border border-surface-hover rounded-md px-3 py-2 text-sm focus:border-gold outline-none">
                <option value="">Select owner…</option>
                {roleOwners.map((t) => <option key={t.id} value={t.name}>{t.name} ({t.employee_id})</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slatetext">Cancel</Button>
            <Button data-testid="save-goal" onClick={submit} disabled={saveMut.isPending} className="bg-gold text-navy hover:bg-gold-hover">Save Goal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
